/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.core.network.auth

import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.auth.OtpType
import io.github.jan.supabase.auth.auth
import io.github.jan.supabase.auth.providers.builtin.OTP
import io.github.jan.supabase.auth.user.UserSession
import io.github.jan.supabase.functions.functions
import io.ktor.client.statement.bodyAsText
import io.ktor.http.isSuccess
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.long
import kotlinx.serialization.json.put
import io.github.jan.supabase.auth.status.SessionStatus as SbSessionStatus

class SupabaseAuthService(private val client: SupabaseClient) : AuthService {

    override val sessionStatus: Flow<SessionStatus> = client.auth.sessionStatus.map { status ->
        when (status) {
            is SbSessionStatus.Initializing -> SessionStatus.Initializing
            is SbSessionStatus.Authenticated -> SessionStatus.Authenticated
            is SbSessionStatus.NotAuthenticated,
            is SbSessionStatus.RefreshFailure,
            -> SessionStatus.NotAuthenticated
        }
    }

    override val currentUserId: String?
        get() = client.auth.currentUserOrNull()?.id

    override val currentUserEmail: String?
        get() = client.auth.currentUserOrNull()?.email

    override suspend fun sendOtp(email: String): Result<Unit> = runCatching {
        if (email.trim().lowercase() == REVIEWER_EMAIL) return@runCatching
        client.auth.signInWith(OTP) {
            this.email = email
        }
    }

    override suspend fun verifyOtp(email: String, code: String): Result<Unit> = runCatching {
        if (email.trim().lowercase() == REVIEWER_EMAIL && code == REVIEWER_OTP) {
            verifyHardcodedOtp(email, code)
            return@runCatching
        }
        client.auth.verifyEmailOtp(
            type = OtpType.Email.EMAIL,
            email = email,
            token = code,
        )
    }

    override suspend fun signOut() {
        client.auth.signOut()
    }

    private suspend fun verifyHardcodedOtp(email: String, otp: String) {
        val response = client.functions.invoke(
            function = HARDCODED_OTP_FUNCTION,
            body = buildJsonObject {
                put("email", email)
                put("otp", otp)
            },
        )
        check(response.status.isSuccess()) {
            "hardcoded-otp function returned ${response.status}"
        }
        val json = Json.decodeFromString<JsonObject>(response.bodyAsText())
        val accessToken = json["access_token"]?.jsonPrimitive?.content
            ?: error("Missing access_token in hardcoded-otp response")
        val refreshToken = json["refresh_token"]?.jsonPrimitive?.content
            ?: error("Missing refresh_token in hardcoded-otp response")
        val expiresIn = json["expires_in"]?.jsonPrimitive?.long ?: 3600L
        val tokenType = json["token_type"]?.jsonPrimitive?.content ?: "bearer"
        val userInfo = client.auth.retrieveUser(accessToken)
        client.auth.importSession(
            UserSession(
                accessToken = accessToken,
                refreshToken = refreshToken,
                expiresIn = expiresIn,
                tokenType = tokenType,
                user = userInfo,
            ),
        )
    }

    private companion object {
        private const val REVIEWER_EMAIL = "oddpodstesting@gmail.com"
        private const val REVIEWER_OTP = "123456"
        private const val HARDCODED_OTP_FUNCTION = "hardcoded-otp"
    }
}
